import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/20/solid';
import { Spinner, Switch } from '../../../components/UI';
import { isMktError, marketingPublic, MktPreferencePage } from '../../../services/marketing/marketing';
import { PublicMessage, PublicShell } from './components/PublicShell.component';

type Save = { state: 'idle' } | { state: 'saving' } | { state: 'saved' } | { state: 'error'; message: string };

// #region EmailPreferences
/**
 * /email/preferences/:token — behind "Manage preferences" in every marketing email.
 * No login: the signed token is the key. Every change saves straight away.
 */
const EmailPreferences: React.FunctionComponent<{}> = (): React.ReactElement => {
    const { token = '' } = useParams<{ token: string }>();
    // From the email's Unsubscribe link: offer the one-press unsubscribe first. Opening the link never unsubscribes by itself.
    const [params] = useSearchParams();
    const arrivedToLeave = params.get('unsubscribe') === '1';
    const [page, setPage] = React.useState<MktPreferencePage | null>(null);
    const [loadError, setLoadError] = React.useState<string | null>(null);
    const [save, setSave] = React.useState<Save>({ state: 'idle' });
    const seq = React.useRef(0);

    React.useEffect(() => {
        document.title = 'Email preferences';
        let live = true;
        (async () => {
            const r = await marketingPublic.preferences(token);
            if (!live) return;
            if (isMktError(r)) setLoadError(r.message); else setPage(r);
        })();
        return () => { live = false; };
    }, [token]);

    const send = async (input: Parameters<typeof marketingPublic.savePreferences>[1], optimistic?: MktPreferencePage): Promise<void> => {
        const id = ++seq.current;
        const before = page;
        if (optimistic) setPage(optimistic);
        setSave({ state: 'saving' });
        const r = await marketingPublic.savePreferences(token, input);
        if (id !== seq.current) return;
        if (isMktError(r)) { if (before) setPage(before); setSave({ state: 'error', message: r.message }); return; }
        setPage(r);
        setSave({ state: 'saved' });
    };

    if (loadError) {
        return <PublicShell><PublicMessage title="This link has expired" body={`${loadError} Use the “Manage preferences” link in a recent email to get a fresh one.`} /></PublicShell>;
    }
    if (!page) return <PublicShell><div className="flex justify-center py-16"><Spinner show={true} /></div></PublicShell>;

    const toggle = (key: string, on: boolean): void => {
        void send({ topics: { [key]: on } }, { ...page, topics: page.topics.map((t) => (t.key === key ? { ...t, subscribed: on } : t)) });
    };

    return (
        <PublicShell workspace={page.workspace}>
            <div className="px-5 pb-2 pt-6 sm:px-7">
                <h1 className="text-xl font-semibold tracking-tight">Your email preferences</h1>
                <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">For <span className="font-medium text-zinc-800 dark:text-zinc-200">{page.email_masked}</span>. Changes save as you make them.</p>
                <SaveStatus save={save} />
            </div>

            {arrivedToLeave && !page.unsubscribed_all ? (
                <div className="mx-5 mb-2 mt-3 rounded-xl border border-surface-border bg-surface px-4 py-4 sm:mx-7">
                    <p className="text-sm font-medium">Stop all marketing email from {page.workspace.name}?</p>
                    <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">Or turn off only the topics you don&apos;t want below.</p>
                    <button type="button" disabled={save.state === 'saving'} onClick={() => void send({ unsubscribe_all: true })}
                        className="mt-3 inline-flex w-full items-center justify-center rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60 sm:w-auto">
                        Unsubscribe from all
                    </button>
                </div>
            ) : null}

            {page.unsubscribed_all ? (
                <div className="mx-5 mb-5 mt-3 rounded-xl border border-surface-border bg-surface px-4 py-4 sm:mx-7">
                    <p className="text-sm font-medium">You&apos;re unsubscribed from all {page.workspace.name} marketing email.</p>
                    <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">You&apos;ll still get emails about your orders and account.</p>
                    <button type="button" disabled={save.state === 'saving'} onClick={() => void send({ resubscribe: true })}
                        className="mt-3 inline-flex w-full items-center justify-center rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60 sm:w-auto">
                        Resubscribe
                    </button>
                </div>
            ) : (
                <>
                    <ul className="mt-2 divide-y divide-surface-border border-y border-surface-border">
                        {page.topics.map((t) => (
                            <li key={t.key}>
                                <label className="flex cursor-pointer items-start gap-4 px-5 py-4 sm:px-7">
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-medium">{t.name}</span>
                                        {t.description ? <span className="mt-0.5 block text-sm text-zinc-600 dark:text-zinc-400">{t.description}</span> : null}
                                    </span>
                                    <Switch color="emerald" className="mt-0.5" checked={t.subscribed} aria-label={`${t.name}: ${t.subscribed ? 'subscribed' : 'not subscribed'}`} onChange={(on: boolean) => toggle(t.key, on)} />
                                </label>
                            </li>
                        ))}
                        {page.topics.length === 0 ? <li className="px-5 py-4 text-sm text-zinc-600 sm:px-7 dark:text-zinc-400">There are no topics to choose from right now.</li> : null}
                    </ul>
                    <div className="px-5 py-5 sm:px-7">
                        <button type="button" disabled={save.state === 'saving'} onClick={() => void send({ unsubscribe_all: true })}
                            className="w-full rounded-lg border border-zinc-950/10 px-4 py-2.5 text-sm font-medium text-zinc-800 hover:bg-zinc-950/[3%] disabled:opacity-60 sm:w-auto dark:border-white/15 dark:text-zinc-200 dark:hover:bg-white/5">
                            Unsubscribe from all marketing email
                        </button>
                        <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">Emails about your orders and account still arrive.</p>
                    </div>
                </>
            )}
        </PublicShell>
    );
};

// #region SaveStatus
const SaveStatus: React.FunctionComponent<{ save: Save }> = ({ save }): React.ReactElement => (
    <p role="status" aria-live="polite" className="mt-2 h-5 text-sm">
        {save.state === 'saving' ? <span className="text-zinc-500 dark:text-zinc-400">Saving…</span> : null}
        {save.state === 'saved' ? <span className="inline-flex items-center gap-1 font-medium text-emerald-700 dark:text-emerald-400"><CheckIcon className="h-4 w-4" aria-hidden="true" />Saved</span> : null}
        {save.state === 'error' ? <span className="text-red-700 dark:text-red-300">{save.message}</span> : null}
    </p>
);

export default EmailPreferences;
